import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { Eye, EyeOff, UserPlus, BookOpen, Check, X } from 'lucide-react';
import { registerSchema } from '../../validations/auth.validation';
import * as authApi from '../../api/auth';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import useTranslation from '../../hooks/useTranslation';

const checkPasswordStrength = (password) => {
  const requirements = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };
  const score = Object.values(requirements).filter(Boolean).length;
  return { score, requirements };
};

const getStrengthInfo = (score) => {
  if (score <= 1) return { label: 'Weak', color: 'bg-red-500' };
  if (score <= 2) return { label: 'Fair', color: 'bg-orange-500' };
  if (score <= 3) return { label: 'Good', color: 'bg-yellow-500' };
  if (score <= 4) return { label: 'Strong', color: 'bg-green-500' };
  return { label: 'Very Strong', color: 'bg-green-600' };
};

const RequirementItem = ({ met, text }) => (
  <div className={`flex items-center gap-1 ${met ? 'text-green-600' : 'text-gray-400'}`}>
    {met ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
    <span>{text}</span>
  </div>
);

const SignupPage = () => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, requirements: {} });
  const navigate = useNavigate();

  const initialValues = { name: '', email: '', password: '', cPassword: '', phone: '' };

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      await authApi.register(values);
      navigate('/login', { replace: true });
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Registration failed';
      if (errorMessage.toLowerCase().includes('email')) setFieldError('email', errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordChange = (e, handleChange) => {
    handleChange(e);
    setPasswordStrength(checkPasswordStrength(e.target.value));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 bg-primary-600 rounded-xl flex items-center justify-center shadow-lg">
              <BookOpen className="h-10 w-10 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">{t('auth.createAccount')}</h1>
          <p className="mt-2 text-gray-600">{t('auth.joinStudySpace')}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <Formik initialValues={initialValues} validationSchema={registerSchema} onSubmit={handleSubmit}>
            {({ isSubmitting, errors, touched, handleChange, values }) => (
              <Form className="space-y-5">
                <div>
                  <label htmlFor="name" className="label">{t('auth.name')} <span className="text-red-500">*</span></label>
                  <Field id="name" name="name" type="text" autoComplete="name"
                    placeholder={t('auth.name')}
                    className={`input-base ${errors.name && touched.name ? 'input-error' : ''}`} />
                  <ErrorMessage name="name" component="p" className="error-message" />
                </div>

                <div>
                  <label htmlFor="email" className="label">{t('auth.email')} <span className="text-red-500">*</span></label>
                  <Field id="email" name="email" type="email" autoComplete="email"
                    placeholder={t('auth.email')}
                    className={`input-base ${errors.email && touched.email ? 'input-error' : ''}`} />
                  <ErrorMessage name="email" component="p" className="error-message" />
                </div>

                <div>
                  <label htmlFor="phone" className="label">{t('auth.phone')} <span className="text-gray-400">({t('auth.optional')})</span></label>
                  <Field id="phone" name="phone" type="tel" autoComplete="tel"
                    placeholder={t('auth.phone')}
                    className={`input-base ${errors.phone && touched.phone ? 'input-error' : ''}`} />
                  <ErrorMessage name="phone" component="p" className="error-message" />
                </div>

                <div>
                  <label htmlFor="password" className="label">{t('auth.password')} <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <Field id="password" name="password"
                      type={showPassword ? 'text' : 'password'} autoComplete="new-password"
                      placeholder={t('auth.password')}
                      onChange={(e) => handlePasswordChange(e, handleChange)}
                      className={`input-base pe-12 ${errors.password && touched.password ? 'input-error' : ''}`} />
                    <button type="button" onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  <ErrorMessage name="password" component="p" className="error-message" />
                  {values.password && (
                    <div className="mt-2">
                      <div className="flex gap-1 mb-1">
                        {[1, 2, 3, 4, 5].map((level) => (
                          <div key={level}
                            className={`h-1 flex-1 rounded ${level <= passwordStrength.score ? getStrengthInfo(passwordStrength.score).color : 'bg-gray-200'}`} />
                        ))}
                      </div>
                      <p className="text-xs text-gray-600">
                        {t('auth.passwordStrength')}: <span className="font-medium">{getStrengthInfo(passwordStrength.score).label}</span>
                      </p>
                      <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
                        <RequirementItem met={passwordStrength.requirements.minLength} text="8+ characters" />
                        <RequirementItem met={passwordStrength.requirements.hasUppercase} text="Uppercase letter" />
                        <RequirementItem met={passwordStrength.requirements.hasLowercase} text="Lowercase letter" />
                        <RequirementItem met={passwordStrength.requirements.hasNumber} text="Number" />
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="cPassword" className="label">{t('auth.confirmPassword')} <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <Field id="cPassword" name="cPassword"
                      type={showConfirmPassword ? 'text' : 'password'} autoComplete="new-password"
                      placeholder={t('auth.confirmPassword')}
                      className={`input-base pe-12 ${errors.cPassword && touched.cPassword ? 'input-error' : ''}`} />
                    <button type="button" onClick={() => setShowConfirmPassword((prev) => !prev)}
                      className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  <ErrorMessage name="cPassword" component="p" className="error-message" />
                </div>

                <button type="submit" disabled={isSubmitting} className="btn-primary w-full flex items-center justify-center gap-2 py-3">
                  {isSubmitting ? (
                    <><LoadingSpinner size="sm" className="text-white" /><span>{t('auth.creatingAccount')}</span></>
                  ) : (
                    <><UserPlus className="h-5 w-5" /><span>{t('auth.createAccount')}</span></>
                  )}
                </button>
              </Form>
            )}
          </Formik>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              {t('auth.alreadyHaveAccount')}{' '}
              <Link to="/login" className="font-medium text-primary-600 hover:text-primary-700">{t('auth.signIn')}</Link>
            </p>
          </div>
        </div>

        <p className="mt-6 text-center text-sm text-gray-500">
          &copy; {new Date().getFullYear()} {t('app.name')}. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default SignupPage;
